import { Box, InputBase } from "@mui/material";
import type { CustomCellEditorProps } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";

import {
	validateAngle,
	validateAngleValue,
} from "@/shared/utils/angleValidation";
import { combineAngle } from "@/shared/utils/angle";
import { getAngleError, readAngleParts } from "../../utils/SectionSpecAngle";
import type { SpecItemDraftRow } from "../../models/SectionSpecForm";

export const formatSpecAngle = (raw: string) => {
	if (raw.trim() === "") return "";
	// Do not round an out-of-range decimal into an apparently valid angle.
	if (!raw.includes("°") && validateAngleValue(Number(raw))) return raw;
	const [degrees, minutes] = readAngleParts(raw);
	return `${degrees}° ${minutes}′`;
};

/** Edit display units only; the form and API still use decimal degrees (SQL decimal(9,4)). */
export const SectionSpecAngleEditor = ({
	initialValue,
	onValueChange,
	data,
	colDef,
	eventKey,
}: CustomCellEditorProps<SpecItemDraftRow, string>) => {
	const [[degrees, minutes], setParts] = useState(() =>
		readAngleParts(initialValue ?? ""),
	);
	const entryInput = useRef<HTMLInputElement>(null);
	const validation = validateAngle(degrees, minutes);
	useEffect(() => {
		const input = entryInput.current;
		if (!input) return;
		input.focus();
		input.select();
	}, [eventKey]);
	const update = (nextDegrees: string, nextMinutes: string) => {
		setParts([nextDegrees, nextMinutes]);
		if (nextDegrees.trim() === "" && nextMinutes.trim() === "") {
			onValueChange("");
			return;
		}
		const valid = getAngleError(nextDegrees, nextMinutes) === undefined;
		// Preserve invalid input so the existing form resolver blocks Save rather than dropping it.
		onValueChange(
			valid
				? (combineAngle(nextDegrees, nextMinutes)?.toFixed(4) ?? "")
				: `${nextDegrees}° ${nextMinutes}′`,
		);
	};
	return (
		<Box
			data-spec-angle-editor
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				height: "100%",
				width: "100%",
			}}
		>
			{(["Deg", "Min"] as const).map((part, index) => (
				<InputBase
					key={part}
					inputRef={
						index === (eventKey === "ArrowLeft" ? 1 : 0)
							? entryInput
							: undefined
					}
					placeholder={part}
					value={index === 0 ? degrees : minutes}
					error={validation.invalidParts?.includes(
						index === 0 ? "Degrees" : "Minutes",
					)}
					onChange={(event) =>
						index === 0
							? update(event.target.value, minutes)
							: update(degrees, event.target.value)
					}
					inputProps={{
						"aria-label": `${data.Code} ${colDef.headerName} ${part}`,
						"data-angle-part": part,
						inputMode: "numeric",
						onFocus: (event) => event.currentTarget.select(),
						"aria-invalid":
							validation.invalidParts?.includes(
								index === 0 ? "Degrees" : "Minutes",
							) ?? false,
						title:
							validation.message ??
							(index === 0 ? "Whole degrees (0–360)" : "Whole minutes (0–59)"),
					}}
					endAdornment={index === 0 ? "°" : "′"}
					sx={{ flex: 1, minWidth: 0, fontSize: "inherit" }}
				/>
			))}
		</Box>
	);
};
